import { AxiosResponse } from 'axios';

import { getAxiosConfig } from '../apisSetup/apiConfig';
import { PATHS } from 'AppConstants';
import { VIEW_ASSIGNED_USERS_INITIAL_PAGE, VIEW_ASSIGNED_USERS_PAGE_LIMIT } from '../RoleAccess/config';

import type {
	RoleData,
	CreateRoleApiResponse,
	GetAllRolesApiResponse,
	GetRolesAggregatedApiResponse,
	GetEmployeeActiveRolesRequest,
	GetEmployeeActiveRolesApiResponse,
	GetRoleEmployeesApiResponse
} from '../RoleAccess/types';

const ccbAxiosApiCaller = getAxiosConfig(PATHS.CCB_URL, true, {}, false);

/**
 * @function createOrUpdateRoleApi
 *
 * @description - Creates or updates a role with permissions
 * @param {RoleData} roleData - The role data object containing roleId, roleName, roleDesc, roleType, and permissions
 * @return {Promise<AxiosResponse<CreateRoleApiResponse>>} - The promise object with role creation response
 */
export const createOrUpdateRoleApi = (
	roleData: RoleData
): Promise<AxiosResponse<CreateRoleApiResponse>> =>
	ccbAxiosApiCaller.post(PATHS.CREATE_UPDATE_ROLE, roleData);

/**
 * @function getAllRolesApi
 *
 * @description - Fetches all active roles
 * @return {Promise<AxiosResponse<GetAllRolesApiResponse>>} - The promise object with all roles
 */
export const getAllRolesApi = (): Promise<AxiosResponse<GetAllRolesApiResponse>> =>
	ccbAxiosApiCaller.get(PATHS.GET_ALL_ROLES);

/**
 * @function getRolesAggregatedApi
 *
 * @description - Fetches active roles together with aggregated counts
 * (rolesCount, assignedUsers, activePermissions) in a single network call.
 * Used by the Role & Access page; existing modals continue to use getAllRolesApi.
 * @return {Promise<AxiosResponse<GetRolesAggregatedApiResponse>>} - The promise object with roles + counts
 */
export const getRolesAggregatedApi = (): Promise<AxiosResponse<GetRolesAggregatedApiResponse>> =>
	ccbAxiosApiCaller.get(PATHS.GET_ROLES_AGGREGATED);

/**
 * @function deleteRoleApi
 *
 * @description - Deletes a role by roleId
 * @param {number} roleId - The ID of the role to delete
 * @return {Promise<AxiosResponse<CreateRoleApiResponse>>} - The promise object with delete response
 */
export const deleteRoleApi = (roleId: number): Promise<AxiosResponse<CreateRoleApiResponse>> =>
	ccbAxiosApiCaller.delete(`${PATHS.GET_ALL_ROLES}?roleId=${roleId}`);

/**
 * @function getEmployeeActiveRolesApi
 *
 * @description - Fetches the currently assigned roles for the supplied employees
 * @param {GetEmployeeActiveRolesRequest} payload - List of employee emailCommIds
 * @return {Promise<AxiosResponse<GetEmployeeActiveRolesApiResponse>>} - The promise object with active roles per employee
 */
export const getEmployeeActiveRolesApi = (
	payload: GetEmployeeActiveRolesRequest
): Promise<AxiosResponse<GetEmployeeActiveRolesApiResponse>> =>
	ccbAxiosApiCaller.post(PATHS.GET_EMPLOYEE_ROLES, payload);

/**
 * @function getRoleEmployeesApi
 *
 * @description - Fetches employees assigned to a role
 * @param {number} roleId - The role identifier
 * @param {{ page?: number; limit?: number }} [params] - Pagination query params
 * @return {Promise<AxiosResponse<GetRoleEmployeesApiResponse>>} - Assigned users for the role
 */
export const getRoleEmployeesApi = (
	roleId: number,
	params?: { page?: number; limit?: number }
): Promise<AxiosResponse<GetRoleEmployeesApiResponse>> => {
	const { page = VIEW_ASSIGNED_USERS_INITIAL_PAGE, limit = VIEW_ASSIGNED_USERS_PAGE_LIMIT } = params || {};
	return ccbAxiosApiCaller.get(
		`${PATHS.GET_ROLE_EMPLOYEES}?roleId=${roleId}&page=${page}&limit=${limit}`
	);
};
